function zonedParts(date, timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(date);
    const get = (t) => parts.find(x => x.type === t)?.value;
    return { year: Number(get('year')), month: Number(get('month')), day: Number(get('day')), hour: Number(get('hour')), minute: Number(get('minute')), second: Number(get('second')) };
}
function zonedLocalToUtc(year, month, day, hour, minute, timeZone) {
    let ts = Date.UTC(year, month - 1, day, hour, minute, 0);
    for (let i = 0; i < 3; i++) {
        const got = zonedParts(new Date(ts), timeZone);
        const represented = Date.UTC(got.year, got.month - 1, got.day, got.hour, got.minute, got.second || 0);
        const wanted = Date.UTC(year, month - 1, day, hour, minute, 0);
        ts += wanted - represented;
    }
    return new Date(ts);
}
export function recommendSendTiming(timeZone, settings = { days: [2, 3, 4], time: '10:15', minLeadMinutes: 60 }, now = new Date(), userTimeZone = 'Europe/London') {
    if (!timeZone)
        return { known: false, reason: 'Recipient timezone is unknown.' };
    try {
        zonedParts(now, timeZone);
    }
    catch {
        return { known: false, reason: 'Recipient timezone is invalid.' };
    }
    const [hour, minute] = settings.time.split(':').map(Number);
    const allowed = new Set(settings.days.map(Number));
    const local = zonedParts(now, timeZone);
    const base = new Date(Date.UTC(local.year, local.month - 1, local.day));
    const min = now.getTime() + settings.minLeadMinutes * 60_000;
    for (let i = 0; i < 21; i++) {
        const d = new Date(base.getTime() + i * 86400_000);
        if (!allowed.has(d.getUTCDay()))
            continue;
        const candidate = zonedLocalToUtc(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), hour, minute, timeZone);
        if (candidate.getTime() < min)
            continue;
        const recipientLabel = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
        const userLabel = new Intl.DateTimeFormat('en-GB', { timeZone: userTimeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
        return { known: true, sendAt: candidate.toISOString(), recipientLabel, userLabel };
    }
    return { known: false, reason: 'No suitable send window could be calculated.' };
}
export function recommendFollowUpTiming(firstSentAt, timeZone, days = 7, localTime = '10:15', userTimeZone = 'Europe/London') {
    if (!timeZone)
        return { known: false, reason: 'Recipient timezone is unknown.' };
    const sent = new Date(firstSentAt);
    if (Number.isNaN(sent.getTime()))
        return { known: false, reason: 'First-send time is invalid.' };
    try {
        zonedParts(sent, timeZone);
    }
    catch {
        return { known: false, reason: 'Recipient timezone is invalid.' };
    }
    const local = zonedParts(sent, timeZone);
    const day = new Date(Date.UTC(local.year, local.month - 1, local.day) + Math.max(0, days) * 86400_000);
    const [hour, minute] = localTime.split(':').map(Number);
    const candidate = zonedLocalToUtc(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), hour, minute, timeZone);
    const recipientLabel = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
    const userLabel = new Intl.DateTimeFormat('en-GB', { timeZone: userTimeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
    return { known: true, sendAt: candidate.toISOString(), recipientLabel, userLabel };
}
/**
 * Schedule an OOO follow-up a fixed number of Monday-Friday business days
 * after the contact's stated return date. Public holidays are deliberately
 * not inferred because no holiday calendar is configured.
 */
export function recommendBusinessDayFollowUp(returnDate, timeZone, businessDaysAfter = 2, localTime = '10:15', userTimeZone = 'Europe/London') {
    if (!timeZone)
        return { known: false, reason: 'Recipient timezone is unknown.' };
    const match = String(returnDate || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match)
        return { known: false, reason: 'Return date is invalid.' };
    try {
        zonedParts(new Date(), timeZone);
    }
    catch {
        return { known: false, reason: 'Recipient timezone is invalid.' };
    }
    let day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    let remaining = Math.max(0, Math.trunc(businessDaysAfter));
    while (remaining > 0) {
        day = new Date(day.getTime() + 86400_000);
        const weekday = day.getUTCDay();
        if (weekday !== 0 && weekday !== 6)
            remaining--;
    }
    // If zero days are requested and the stated return date is a weekend, use
    // the next working day rather than scheduling on Saturday/Sunday.
    while (day.getUTCDay() === 0 || day.getUTCDay() === 6)
        day = new Date(day.getTime() + 86400_000);
    const [hour, minute] = localTime.split(':').map(Number);
    const candidate = zonedLocalToUtc(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), hour, minute, timeZone);
    const recipientLabel = new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
    const userLabel = new Intl.DateTimeFormat('en-GB', { timeZone: userTimeZone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(candidate);
    return { known: true, sendAt: candidate.toISOString(), recipientLabel, userLabel };
}
